"use client";

import React, { useEffect, useState } from "react";
import { AiOutlineStar, AiFillStar } from "react-icons/ai";
import { H1 } from "~/components/themed/font";
import { api } from "~/utils/api";
import Spinner from "~/components/themed/spinner";
import HostedBy from "~/components/themed/HostedBy";
import { toast } from "react-toastify";
import { useSession } from "next-auth/react";
import LoginModal from "~/components/v3/themed/LoginModal";
import { EventPreview } from "~/components/v3/events-components/EventPreview";
import { Button } from "~/components/v3/themed/Button";
import { If } from "~/components/v3/Conditional";

const HostFeedback = ({ params: { slug } }: { params: { slug: string } }) => {
    const { data: session } = useSession();

    const [showLoginModal, setShowLoginModal] = useState(false);

    const { data: event, isLoading } = api.event.getBySlug.useQuery(
        slug as string,
        {
            enabled: !!slug,
        },
    );

    useEffect(() => {
        setShowLoginModal(!session?.user.id);
    }, [session]);

    const {
        data: isAllowedToSubmitFeedback,
        isLoading: isLoadingIsAllowedToSubmitFeedback,
    } = api.feedback.isAllowedToSubmitFeedback.useQuery(
        {
            eventSlug: slug as string,
        },
        { enabled: !!slug && !!session?.user.id },
    );

    const [feedbacks, setFeedbacks] = useState<
        Record<string, { rating: number; notes?: string }>
    >({});

    const handleStarClick = (hostId: string, rating: number) => {
        setFeedbacks((prevFeedbacks) => ({
            ...prevFeedbacks,
            [hostId]: { rating, notes: prevFeedbacks[hostId]?.notes || "" },
        }));
    };

    const handleNoteChange = (hostId: string, notes: string) => {
        setFeedbacks((prevFeedbacks) => ({
            ...prevFeedbacks,
            [hostId]: { notes, rating: prevFeedbacks[hostId]?.rating || 0 },
        }));
    };

    const [venueFeedback, setVenueFeedback] = useState<{
        rating: number;
        notes?: string;
    }>({ rating: 0, notes: "" });

    const [eventFeedback, setEventFeedback] = useState<{
        experienceRating: number;
        experienceNotes?: string;
        recommendationRating: number;
        recommendationNotes?: string;
    }>({
        experienceRating: 0,
        experienceNotes: "",
        recommendationRating: 0,
        recommendationNotes: "",
    });

    const handleVenueStarClick = (rating: number) => {
        setVenueFeedback((prev) => ({ ...prev, rating }));
    };
    const handleVenueNoteChange = (notes: string) => {
        setVenueFeedback((prev) => ({ ...prev, notes }));
    };
    const handleEventStarClick = (rating: number) => {
        setEventFeedback((prev) => ({ ...prev, experienceRating: rating }));
    };
    const handleEventRecommendationClick = (rating: number) => {
        setEventFeedback((prev) => ({ ...prev, recommendationRating: rating }));
    };
    const handleEventExperienceNotesChange = (notes: string) => {
        setEventFeedback((prev) => ({ ...prev, experienceNotes: notes }));
    };
    const handleEventRecommendationNotesChange = (notes: string) => {
        setEventFeedback((prev) => ({ ...prev, recommendationNotes: notes }));
    };

    const addHostAndVenueFeedbackMutation =
        api.feedback.addEventFeedback.useMutation();
    const handleSubmit = async () => {
        if (!event?.id) {
            throw new Error("Event ID not found");
        }
        toast.loading("Submitting feedback", { toastId: "submit-feedback" });

        const hostFeedbacksArray = Object.entries(feedbacks).map(
            ([hostId, { rating, notes }]) => ({
                hostId,
                rating,
                notes,
            }),
        );

        const payload = {
            eventId: event?.id,
            venueRating: venueFeedback.rating,
            venueNotes: venueFeedback.notes,
            hostFeedbacks: hostFeedbacksArray,
            eventNotes: eventFeedback.experienceNotes,
            eventRecommendationNotes: eventFeedback.recommendationNotes,
            eventFeedback: {
                experienceRating: eventFeedback.experienceRating,
                recommendationRating: eventFeedback.recommendationRating,
            },
        };

        await addHostAndVenueFeedbackMutation.mutateAsync(payload, {
            onSuccess: async () => {
                toast.success("Feedback successfully submitted!");
                if (slug) {
                    window.open(`/events/${slug as string}`, "_self");
                }
            },
            onError: (error) => {
                toast.error(error.message);
            },
            onSettled: () => {
                toast.dismiss("submit-feedback");
            },
        });
    };

    if (showLoginModal) {
        return (
            <LoginModal
                isOpen={showLoginModal}
                setIsOpen={setShowLoginModal}
                title="Log in to provide feedback"
                description="You need to be logged in to provide feedback for the event"
            />
        );
    }

    if (isLoadingIsAllowedToSubmitFeedback || isLoading || !event) {
        return <Spinner />;
    }

    if (!isLoadingIsAllowedToSubmitFeedback) {
        if (isAllowedToSubmitFeedback === false) {
            return (
                <div>
                    <h6>
                        {
                            "You have already submitted feedback for this event :-)"
                        }
                    </h6>
                </div>
            );
        }
    }

    return (
        <div className="min-h-screen bg-v3-N600 p-4 text-v3-N100 sm:p-8">
            <div className="mx-auto max-w-3xl rounded-lg bg-v3-N500 p-6 shadow-lg sm:p-8">
                <H1 className="mb-8 text-3xl font-bold text-v3-primary">
                    Event Feedback
                </H1>

                <EventPreview event={event} />

                <If
                    condition={event?.community?.isEventFeedbackAllowed ?? true}
                >
                    <div className="mb-8">
                        <h4 className="mb-2 text-xl font-semibold">
                            How would you rate this event overall?
                        </h4>
                        <div className="flex w-full flex-row">
                            {[1, 2, 3, 4, 5].map((starIndex) => (
                                <div
                                    key={starIndex}
                                    onClick={() =>
                                        handleEventStarClick(starIndex)
                                    }
                                    className="cursor-pointer"
                                >
                                    {starIndex <=
                                    eventFeedback.experienceRating ? (
                                        <AiFillStar
                                            size={36}
                                            className="text-v3-primary"
                                        />
                                    ) : (
                                        <AiOutlineStar
                                            size={36}
                                            className="text-v3-N300"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        <If
                            condition={
                                event?.community?.isFeedbackNotesAllowed ?? true
                            }
                        >
                            <div className="form-group w-full">
                                <label className="mb-2 block text-v3-N200">
                                    Additional Notes
                                </label>
                                <textarea
                                    className="h-[120px] w-full rounded-md border border-v3-N400 bg-v3-N600 px-3 py-2 text-v3-N100 focus:outline-none focus:ring-2 focus:ring-v3-primary"
                                    placeholder="Event experience was..."
                                    value={eventFeedback.experienceNotes}
                                    onChange={(e) =>
                                        handleEventExperienceNotesChange(
                                            e.target.value,
                                        )
                                    }
                                />
                            </div>
                        </If>
                    </div>
                </If>

                <If
                    condition={
                        event?.community
                            ?.isEventRecommendationFeedbackAllowed ?? true
                    }
                >
                    <div className="mb-8">
                        <h4 className="mb-2 text-xl font-semibold">
                            Would you recommend events in this community to a
                            friend?
                        </h4>
                        <div className="flex w-full flex-row">
                            {[1, 2, 3, 4, 5].map((starIndex) => (
                                <div
                                    key={starIndex}
                                    onClick={() =>
                                        handleEventRecommendationClick(
                                            starIndex,
                                        )
                                    }
                                    className="cursor-pointer"
                                >
                                    {starIndex <=
                                    eventFeedback.recommendationRating ? (
                                        <AiFillStar
                                            size={36}
                                            className="text-v3-primary"
                                        />
                                    ) : (
                                        <AiOutlineStar
                                            size={36}
                                            className="text-v3-N300"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        <If
                            condition={
                                event?.community?.isFeedbackNotesAllowed ?? true
                            }
                        >
                            <div className="form-group w-full">
                                <label className="mb-2 block text-v3-N200">
                                    Additional Notes
                                </label>
                                <textarea
                                    className="h-[120px] w-full rounded-md border border-v3-N400 bg-v3-N600 px-3 py-2 text-v3-N100 focus:outline-none focus:ring-2 focus:ring-v3-primary"
                                    placeholder="I would recommend because..."
                                    value={eventFeedback.recommendationNotes}
                                    onChange={(e) =>
                                        handleEventRecommendationNotesChange(
                                            e.target.value,
                                        )
                                    }
                                />
                            </div>
                        </If>
                    </div>
                </If>

                <If condition={event?.community?.isHostFeedbackAllowed ?? true}>
                    <div className="mb-8">
                        <h4 className="mb-4 text-xl font-semibold">
                            Host Feedback
                        </h4>
                        {event.hosts.map((host) => {
                            let disabled = false;
                            const feedback = feedbacks[host.id] || {
                                rating: 0,
                                notes: "",
                            };

                            if (host.user.id === session?.user.id) {
                                disabled = true;
                            }
                            return (
                                <div
                                    key={host.id}
                                    className="mb-6 flex w-full flex-col items-start gap-4 border-b border-v3-N400 pb-6"
                                >
                                    <div className="mb-2 flex flex-row items-center">
                                        <HostedBy user={host.user} />
                                    </div>
                                    <If condition={disabled}>
                                        <div className="ml-2 text-xs text-v3-N300">
                                            You cannot rate yourself
                                        </div>
                                    </If>
                                    <If condition={!disabled}>
                                        <div className="mb-2 flex w-full flex-row">
                                            {[1, 2, 3, 4, 5].map(
                                                (starIndex) => (
                                                    <div
                                                        key={starIndex}
                                                        onClick={() =>
                                                            handleStarClick(
                                                                host.id,
                                                                starIndex,
                                                            )
                                                        }
                                                        className="cursor-pointer"
                                                    >
                                                        {starIndex <=
                                                        feedback.rating ? (
                                                            <AiFillStar
                                                                size={36}
                                                                className="text-v3-primary"
                                                            />
                                                        ) : (
                                                            <AiOutlineStar
                                                                size={36}
                                                                className="text-v3-N300"
                                                            />
                                                        )}
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </If>
                                    <If
                                        condition={
                                            event?.community
                                                ?.isFeedbackNotesAllowed ?? true
                                        }
                                    >
                                        <div className="form-group w-full">
                                            <label className="mb-2 block text-v3-N200">
                                                Additional Notes
                                            </label>
                                            <textarea
                                                className="h-[120px] w-full rounded-md border border-v3-N400 bg-v3-N600 px-3 py-2 text-v3-N100 focus:outline-none focus:ring-2 focus:ring-v3-primary"
                                                placeholder={`${host.user.name} was...`}
                                                value={feedback.notes}
                                                onChange={(e) =>
                                                    handleNoteChange(
                                                        host.id,
                                                        e.target.value,
                                                    )
                                                }
                                                disabled={disabled}
                                            />
                                        </div>
                                    </If>
                                </div>
                            );
                        })}
                    </div>
                </If>

                <If
                    condition={event?.community?.isVenueFeedbackAllowed ?? true}
                >
                    <div className="mb-8">
                        <h4 className="mb-2 text-xl font-semibold">
                            How was the venue?
                        </h4>
                        <div className="mb-4 flex w-full flex-row">
                            {[1, 2, 3, 4, 5].map((starIndex) => (
                                <div
                                    key={starIndex}
                                    onClick={() =>
                                        handleVenueStarClick(starIndex)
                                    }
                                    className="cursor-pointer"
                                >
                                    {starIndex <= venueFeedback.rating ? (
                                        <AiFillStar
                                            size={36}
                                            className="text-v3-primary"
                                        />
                                    ) : (
                                        <AiOutlineStar
                                            size={36}
                                            className="text-v3-N300"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                        <If
                            condition={
                                event?.community?.isFeedbackNotesAllowed ??
                                false
                            }
                        >
                            <div className="form-group w-full">
                                <label className="mb-2 block text-v3-N200">
                                    Additional Notes
                                </label>
                                <textarea
                                    className="h-[120px] w-full rounded-md border border-v3-N400 bg-v3-N600 px-3 py-2 text-v3-N100 focus:outline-none focus:ring-2 focus:ring-v3-primary"
                                    placeholder="..."
                                    value={venueFeedback.notes}
                                    onChange={(e) =>
                                        handleVenueNoteChange(e.target.value)
                                    }
                                />
                            </div>
                        </If>
                    </div>
                </If>

                <div className="mt-8">
                    <Button
                        onClick={handleSubmit}
                        disabled={addHostAndVenueFeedbackMutation.isLoading}
                        className="w-full rounded-md bg-v3-primary px-4 py-3 font-bold text-v3-N600 transition-colors duration-200 hover:bg-v3-M300 focus:outline-none focus:ring-2 focus:ring-v3-M300 focus:ring-opacity-50"
                    >
                        Submit Feedback
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default HostFeedback;
